/* ─────────────────────────────────────────────────────────────
   NEWS SENTIMENT TAB  (added 2026-10-07)
   GET /api/v1/stocks/{ticker}/news-sentiment?weeks=26

   factor-alpha's weekly news model (news-v1.1): one 0–100 score per ISO
   week describing how the company was WRITTEN ABOUT — five dimensions,
   each backed by a verified quote from a headline + lead paragraph, and
   the stories that produced it. Sibling of the earnings-call sentiment
   on the Earnings Review tab; a different construct, never averaged.

     masthead + band badge   ← latest.composite_score, band, iso_week, window
     summary tiles           ← wow_delta, baseline_z, evidence_grade, volume
     data checks             ← data_checks[]
     26-week trend           ← series[] (null composite = quiet week → gap)
     dimensions table        ← latest.dimensions[] (score, quote, outlet, link)
     stories table           ← latest.stories[] (heaviest first)
     footer                  ← scale_note, model, prompt_version, data_as_of

   Quiet week: composite_score is null and band is "Quiet" — nothing survived
   the noise filters, so no score was issued. Not a score of 50.

   Null rule: any field may be null / any list empty. Render what's present,
   hide what's null, never show "N/A".
───────────────────────────────────────────────────────────── */

import React, { useEffect, useMemo, useState } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ResponsiveContainer,
  Tooltip as ChartTooltip, XAxis, YAxis,
} from "recharts";
import { getNewsSentiment } from "../../../api/stocks_api";
import { fmtDate, fmtNum } from "../../../config/earningsNote";
import {
  NotePage, SectionTitle, Card, TileGrid, Banner, Pill,
  Scroller, Table, Th, Td, Tl, NoteSkeleton, NoteEmpty,
  NoteMasthead, NoteFooter,
} from "../Widgets/earningsNoteUI";

/* Same cut points as the earnings-call model, so a reader learns one scale. */
const BAND_COLOR = {
  "Very Positive": "#047857",
  Positive: "#059669",
  Constructive: "#65a30d",
  "Neutral / Mixed": "#6b7280",
  Cautious: "#d97706",
  Negative: "#dc2626",
  Quiet: "#4b5563",
};
const GRADE_LABEL = { filing: "Regulatory filing", issuer: "Company statement", press: "Press report", "-": "No evidence" };
const TIER_LABEL = { A: "Tier A · primary wire / journalism", B: "Tier B · market press", C: "Tier C · template content" };

const fmt1 = (v) => (v == null ? null : Number(v).toFixed(1));
const signed = (v) => (v == null ? null : `${Number(v) > 0 ? "+" : ""}${Number(v).toFixed(1)}`);
const dimTone = (d) => (!d?.has_evidence ? "muted" : Number(d.score) >= 6 ? "pos" : Number(d.score) <= 4 ? "neg" : "muted");

/* ── Trend chart: 26 weeks, neutral band shaded, gaps on quiet weeks ── */
const TrendChart = ({ series }) => {
  const data = useMemo(
    () => (series || []).map((p) => ({ ...p, label: String(p.iso_week || "").replace(/^\d{4}-/, "") })),
    [series]
  );
  if (!data.length) return null;
  return (
    <Card>
      <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)", marginBottom: 8 }}>
        Weekly composite, last {data.length} weeks · shaded band = Neutral / Mixed (45–55) · gaps are quiet weeks
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-primary)" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "var(--color-text-muted)" }} interval="preserveStartEnd" />
          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "var(--color-text-muted)" }} />
          <ReferenceArea y1={45} y2={55} fill="#6b7280" fillOpacity={0.15} />
          <ChartTooltip
            contentStyle={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border-primary)", fontSize: 12 }}
            formatter={(v, _n, p) => [
              v == null ? "quiet" : Number(v).toFixed(1),
              `${p?.payload?.band ?? ""}${p?.payload?.wow_delta != null ? ` (${signed(p.payload.wow_delta)} w/w)` : ""}`,
            ]}
            labelFormatter={(l, p) =>
              p && p[0] ? `${p[0].payload.iso_week} · ${fmtDate(p[0].payload.week_start)} → ${fmtDate(p[0].payload.week_end)}` : l
            }
          />
          <Line
            type="monotone" dataKey="composite_score" stroke="var(--color-primary)" strokeWidth={2}
            dot={{ r: 3 }} connectNulls={false} isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

/* ── Pure view: everything below renders from the payload alone (testable without effects) ── */
export const NewsSentimentView = ({ ticker, data }) => {
  if (!data?.covered || !data?.latest)
    return <NoteEmpty>{data?.data_checks?.[0] ?? `No news-sentiment score has been published for ${ticker} yet.`}</NoteEmpty>;

  const { latest, series = [], data_checks: checks = [], scale_note, data_as_of } = data;
  const quiet = !!latest.quiet;
  const dims = latest.dimensions ?? [];
  const stories = latest.stories ?? [];
  const vol = latest.volume ?? {};
  const dimLabel = Object.fromEntries(dims.map((d) => [d.key, d.label]));

  /* ── Summary tiles (null rule: drop what is missing) ── */
  const tiles = [
    latest.wow_delta != null ? { label: "Week over week", value: signed(latest.wow_delta), hint: "score points vs the previous scored week" } : null,
    latest.baseline_z != null
      ? { label: "vs own history (z)", value: fmtNum(latest.baseline_z, 2), hint: "standard deviations from the name's trailing weeks" }
      : null,
    latest.evidence_grade ? { label: "Best evidence", value: GRADE_LABEL[latest.evidence_grade] ?? latest.evidence_grade } : null,
    vol.n_raw != null
      ? {
          label: "Coverage this week",
          value: `${vol.n_scored ?? 0} scored`,
          hint: `${vol.n_raw} items · ${vol.n_kept} after filters · ${vol.n_clusters} stories — volume is confidence, not signal`,
        }
      : null,
    latest.dims_evidenced != null ? { label: "Dimensions evidenced", value: `${latest.dims_evidenced} of 5` } : null,
  ].filter(Boolean);

  return (
    <NotePage>
      <NoteMasthead
        eyebrow={`News sentiment · week ${latest.iso_week}${latest.window ? ` · ${fmtDate(latest.window.start)} → ${fmtDate(latest.window.end)}` : ""}`}
        title={quiet ? "Quiet week — no score issued" : `${fmt1(latest.composite_score)} / 100`}
        badge={latest.band}
        badgeColor={BAND_COLOR[latest.band] ?? "var(--color-primary)"}
        meta="How the press wrote about the company this week — not what management said. Not comparable with the earnings-call score."
      />

      {checks.length > 0 && (
        <Banner kind="warning" style={{ marginTop: "var(--spacing-md)" }}>
          {checks.map((c, i) => (
            <div key={i}>{c}</div>
          ))}
        </Banner>
      )}

      {tiles.length > 0 && (
        <div style={{ marginTop: "var(--spacing-md)" }}>
          <TileGrid tiles={tiles} min={150} />
        </div>
      )}

      {series.length > 1 && (
        <>
          <SectionTitle caption="one point per ISO week">Trend</SectionTitle>
          <TrendChart series={series} />
        </>
      )}

      {dims.length > 0 && (
        <>
          <SectionTitle caption="0–10 within each dimension; 5 = neutral, nothing addressed it">Five dimensions</SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Dimension</Th>
                  <Th>Weight</Th>
                  <Th>Score</Th>
                  <Th>Stories</Th>
                  <Th left>Evidence</Th>
                </tr>
              </thead>
              <tbody>
                {dims.map((d) => (
                  <tr key={d.key} style={{ opacity: d.has_evidence ? 1 : 0.6 }}>
                    <Tl>{d.label}</Tl>
                    <Td>{d.weight != null ? `${Math.round(d.weight * 100)}%` : null}</Td>
                    <Td bold tone={dimTone(d)}>{d.has_evidence ? fmt1(d.score) : "—"}</Td>
                    <Td>{d.n_stories ?? 0}</Td>
                    <Td left wrap>
                      {d.has_evidence ? (
                        <>
                          <div style={{ color: "var(--color-text-primary)" }}>“{d.quote}”</div>
                          <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)", marginTop: 2 }}>
                            {d.publisher}
                            {d.publisher_tier ? ` · ${TIER_LABEL[d.publisher_tier] ?? d.publisher_tier}` : ""}
                            {d.evidence_grade ? ` · ${GRADE_LABEL[d.evidence_grade] ?? d.evidence_grade}` : ""}
                            {d.url ? (
                              <>
                                {" · "}
                                <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-primary)" }}>
                                  {d.title ?? "source"}
                                </a>
                              </>
                            ) : null}
                            {d.quote_verified ? " · quote verified" : ""}
                          </div>
                        </>
                      ) : (
                        <span style={{ color: "var(--color-text-muted)" }}>Nothing in this week's coverage addressed it.</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroller>
        </>
      )}

      {stories.length > 0 && (
        <>
          <SectionTitle caption="heaviest first · weight = outlet tier × materiality × recency × evidence grade">Stories that moved the score</SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Published</Th>
                  <Th left>Story</Th>
                  <Th left>Source</Th>
                  <Th>Materiality</Th>
                  <Th>Weight</Th>
                  <Th left>Dimensions</Th>
                </tr>
              </thead>
              <tbody>
                {stories.map((s, i) => (
                  <tr key={`${s.url ?? s.title}-${i}`}>
                    <Td left>{fmtDate(String(s.published ?? "").slice(0, 10))}</Td>
                    <Td left wrap>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-primary)" }}>
                          {s.title}
                        </a>
                      ) : (
                        s.title
                      )}
                      {s.n_sources > 1 ? (
                        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}> · {s.n_sources} outlets</span>
                      ) : null}
                    </Td>
                    <Td left wrap>
                      {s.publisher}
                      <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}>
                        {s.publisher_tier ? `Tier ${s.publisher_tier}` : ""}
                        {s.evidence_grade ? ` · ${GRADE_LABEL[s.evidence_grade] ?? s.evidence_grade}` : ""}
                      </div>
                    </Td>
                    <Td>{s.materiality != null ? `${s.materiality}/5` : null}</Td>
                    <Td>{s.weight != null ? fmtNum(s.weight, 2) : null}</Td>
                    <Td left wrap>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {Object.entries(s.dims ?? {}).map(([k, v]) => (
                          <MuiTooltip key={k} title={v?.quote ? `“${v.quote}”` : ""} arrow>
                            <span>
                              <Pill tone={Number(v?.score) >= 6 ? "pos" : Number(v?.score) <= 4 ? "neg" : "muted"}>
                                {dimLabel[k] ?? k}: {fmt1(v?.score)}
                              </Pill>
                            </span>
                          </MuiTooltip>
                        ))}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroller>
        </>
      )}

      <NoteFooter>
        {scale_note ? <div>{scale_note}</div> : null}
        <div>
          Sources: FMP stock news and press releases for the week; template content (ratings-change posts, 13F write-ups, listicles) is excluded before scoring.
          {latest.model ? ` Judged by ${latest.model}` : ""}
          {latest.prompt_version ? ` (${latest.prompt_version})` : ""}
          {data_as_of ? `; data as of ${fmtDate(data_as_of)}.` : "."}
        </div>
      </NoteFooter>
    </NotePage>
  );
};

const NewsSentiment = ({ stock }) => {
  const ticker = stock?.ticker;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ error: false, notFound: false });

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setState({ error: false, notFound: false });
      const res = await getNewsSentiment(ticker);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else setState({ error: !res?.notFound, notFound: !!res?.notFound });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) return <NoteSkeleton blocks={[90, 120, 240, 260]} />;
  if (state.notFound) return <NoteEmpty>{ticker} is not in coverage.</NoteEmpty>;
  if (state.error || !data) return <NoteEmpty>News sentiment is not available for {ticker}.</NoteEmpty>;
  return <NewsSentimentView ticker={ticker} data={data} />;
};

export default NewsSentiment;
